import {defineStore} from 'pinia'
import type {Story} from '~/types/story';
import {Mutex} from "async-mutex";

export const useStoryStore = defineStore('stories', () => {
    const auth = useAuthStore();
    console.trace(`Stories store - user session: ${auth.user?.email}`);

    const config = useRuntimeConfig();
    const localStoreName = 'itlm-v1-stories';
    const userStories = ref<Story[]>([]);
    const isLoaded = ref(false);
    const mutex = new Mutex();

    watch(() => auth.user, async (newUser) => {
        if (newUser) {
            console.trace("Stories store - User logged in or updated:", newUser.email);
            await loadValues();
        } else {
            console.trace("Stories store - User logged out");
        }
    }, { deep: true }); // Use deep: true if you want to detect changes INSIDE the user object

    const loadValues = async () => {
        const release = await mutex.acquire();
        try {
            const saved = localStorage.getItem(localStoreName);
            if (saved) {
                console.trace('Found saved ...')
                try {
                    userStories.value = JSON.parse(saved);
                } catch (error) {
                    console.error("Failed to parse stories", error);
                }
            } else if (auth.user) {
                console.trace('Not found saved, loading from API ...')
                try {
                    userStories.value = await $fetch<Story[]>('/api/person', {
                        baseURL: config.public.apiBase,
                        headers: {
                            Authorization: `Bearer ${auth.user.accessToken}`,
                            'Authorization-Type': `${auth.user.type}`,
                        }
                    });
                } catch (error: any) {
                    if (error.status === 403) {
                        auth.logout()
                    }
                }
            }
            isLoaded.value = true;
        } finally {
            release();
        }
    }

    const saveToDisk = () => {
        localStorage.setItem(localStoreName, JSON.stringify(userStories.value));
    }

    watch(userStories, () => {
        if (isLoaded.value) {
            saveToDisk();
        }
    }, {deep: true});

    // Adds a new story to the start of the list
    const addStory = (story: Story) => {
        userStories.value.unshift(story);
    };

    // Removes a story by UUID
    const removeStory = (uuid: string) => {
        if (userStories.value) {
            userStories.value = userStories.value.filter(s => s.uuid !== uuid);
        }
    };

    // Replaces an existing story (useful for updates)
    const replaceStory = (updatedStory: Story) => {
        if (userStories.value) {
            const index = userStories.value.findIndex(s => s.uuid === updatedStory.uuid);
            if (index !== -1) {
                userStories.value[index] = updatedStory;
            } else {
                userStories.value.unshift(updatedStory);
            }
        }
    };

    const clearStories = () => {
        isLoaded.value = false;
        userStories.value = [];
        localStorage.removeItem(localStoreName);
    };

    const fixImagePath = (path: string | undefined) => {
        if (path) {
            if (path.startsWith('http')) {
                return new URL(path).pathname
            } else if (!path.startsWith('/')) {
                return '/' + path;
            }
        } else {
            return '/assets/img/generic-person.jpeg';
        }
        return path;
    }

    const getStoryImage = (story: Story) => {
        let result = '/assets/img/generic-person.jpeg';
        if (story.mainPhotoUrl) {
            result = fixImagePath(story.mainPhotoUrl)
        } else if (story.photoUrls?.length > 0) result = fixImagePath(story.photoUrls[0]);
        return result;
    };


    return {
        userStories,
        isLoaded,
        loadValues,
        addStory,
        removeStory,
        replaceStory,
        clearStories,
        getStoryImage
    };
});
